import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface StatusBreakdown {
  readonly [status: string]: number;
}
interface QueueHealth {
  readonly pending: number;
  readonly processing: number;
  readonly completed: number;
  readonly failed: number;
  readonly dlq: number;
  readonly backlog: number;
}
interface HealthSnapshot {
  readonly connectors: { total: number; byStatus: StatusBreakdown };
  readonly flows: { total: number; byStatus: StatusBreakdown; lastRunFailures: number };
  readonly queue: QueueHealth;
  readonly webhooks: { total: number; active: number; totalFailures: number };
  readonly sync: { total: number; failed: number; partial: number };
  readonly generatedAt: string;
}
interface AnalyticsSummary {
  readonly totalCalls: number;
  readonly totalSuccess: number;
  readonly totalFailure: number;
  readonly successRate: number;
  readonly avgResponseMs: number;
  readonly p95ResponseMs: number;
  readonly apiCount: number;
}

/** M29 — Integration Dashboard (§33). Operational landing page for the
 * Enterprise Integration Platform: a single-glance summary of connector,
 * flow, queue, webhook and sync activity (§24) alongside API-gateway usage
 * KPIs (§25). */
export function M29IntegrationDashboardPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [health, setHealth] = useState<HealthSnapshot | null>(null);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: HealthSnapshot }>('/v1/eip/monitoring/health', { token }),
      apiRequest<{ data: AnalyticsSummary }>('/v1/eip/analytics/summary', { token }),
    ])
      .then(([h, s]) => { if (alive) { setHealth(h.data); setSummary(s.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="integration-dashboard">
      <h1>{t('m29.dashboard.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !health || !summary ? (
        <p>{t('m29.dashboard.loading')}</p>
      ) : (
        <section aria-label="summary-cards" className="card-grid">
          <article aria-label="card-connectors">
            <h2>{t('m29.dashboard.cardConnectors')}</h2>
            <p aria-label="connectors-total">{t('m29.dashboard.connectorsTotal', { count: health.connectors.total })}</p>
          </article>
          <article aria-label="card-flows">
            <h2>{t('m29.dashboard.cardFlows')}</h2>
            <p aria-label="flows-total">{t('m29.dashboard.flowsTotal', { count: health.flows.total })}</p>
            <p aria-label="flows-failures">{t('m29.dashboard.flowsFailures', { count: health.flows.lastRunFailures })}</p>
          </article>
          <article aria-label="card-queue">
            <h2>{t('m29.dashboard.cardQueue')}</h2>
            <p aria-label="queue-backlog">{t('m29.dashboard.queueBacklog', { count: health.queue.backlog })}</p>
            <p aria-label="queue-dlq">{t('m29.dashboard.queueDlq', { count: health.queue.dlq })}</p>
          </article>
          <article aria-label="card-webhooks">
            <h2>{t('m29.dashboard.cardWebhooks')}</h2>
            <p aria-label="webhooks-active">{t('m29.dashboard.webhooksActive', { count: health.webhooks.active })}</p>
            <p aria-label="webhooks-failures">{t('m29.dashboard.webhooksFailures', { count: health.webhooks.totalFailures })}</p>
          </article>
          <article aria-label="card-sync">
            <h2>{t('m29.dashboard.cardSync')}</h2>
            <p aria-label="sync-total">{t('m29.dashboard.syncTotal', { count: health.sync.total })}</p>
            <p aria-label="sync-partial">{t('m29.dashboard.syncPartial', { count: health.sync.partial })}</p>
          </article>
          <article aria-label="card-api-usage">
            <h2>{t('m29.dashboard.cardApiUsage')}</h2>
            <p aria-label="api-calls">{t('m29.dashboard.apiCalls', { count: summary.totalCalls })}</p>
            <p aria-label="api-success-rate">{t('m29.dashboard.apiSuccessRate', { rate: summary.successRate })}</p>
          </article>
        </section>
      )}
    </main>
  );
}
